import { authModel, GlobalState, globalState } from '../models';
import {
    formatUrlSuffix,
    get,
    getApiUrl,
    insertURLQuery,
    isEmpty,
    isNumber,
    join,
    map,
    QueryKey,
    split,
    SuiteId,
    URL_PARAMETER
} from '@libs/shared';
import { SITE_ROUTER } from '../constants';
import siteRouterManager, { SiteRouterKey } from './site-router';

/**
 * 获取站点接口地址
 * @param url 接口路径
 * @param args 路径参数
 */
export const getRequestUrl = (url: string, ...args: (string | number)[]) => {
    const preUrl = globalState.origin;
    return formatUrlSuffix(preUrl, getApiUrl(url, ...args));
};

/**
 * 获取站点路由
 * @param pattern 路由模板
 * @param args 路径参数
 * @deprecated 推荐使用 `siteRouterManager.getPath` 替代
 */
export const getSiteUrl = (pattern: string, ...args: (string | number)[]) => {
    return getApiUrl(pattern, ...args);
};

/**
 * 获取带参站点地址
 * @deprecated 推荐使用 `siteRouterManager.getQueryPath` 替代
 */
export const getQueryUrl = (
    query: { key: QueryKey; value: string | number }[],
    pattern: string,
    ...args: (string | number)[]
) => {
    const queryStr = join(
        map(query, (item) => {
            return `${item.key}=${item.value}`;
        }),
        '&'
    );
    const routerArr = split(getSiteUrl(pattern, ...args), '#');
    const baseRouter = routerArr.shift();
    return `${baseRouter}?${queryStr}${isEmpty(routerArr) ? '' : '#' + routerArr.join('')}`;
};

/**
 * 获取个人设置地址
 */
export const getPersonalSetUrl = () => {
    const queryParams = [
        {
            key: URL_PARAMETER.REFERER_URI,
            value: encodeURIComponent(window.location.href)
        }
    ];
    return siteRouterManager.getQueryPath(queryParams, SiteRouterKey.Account);
};

/**
 * 获取企业管理后台地址
 */
export const getCorpAdminUrl = (tenantId = authModel.tenantId) => {
    const queryParams = [
        {
            key: URL_PARAMETER.REFERER_URI,
            value: encodeURIComponent(window.location.href)
        }
    ];
    return siteRouterManager.getQueryPath(queryParams, SiteRouterKey.CorpAdmin, tenantId);
};

/**
 * 获取云币订单页地址
 */
export const getCoinOrderUrl = (tenantId = authModel.tenantId) => {
    return siteRouterManager.getQueryPath(
        [
            {
                key: URL_PARAMETER.ORDER_TYPE,
                value: 'coin'
            }
        ],
        SiteRouterKey.OrderCreate,
        tenantId
    );
};

export const getErrorUrl = (errorType: 403 | 404 | 409) => {
    return siteRouterManager.getPath(SiteRouterKey.Error, errorType);
};

export const getForgotPasswordUrl = () => {
    return siteRouterManager.getPath(SiteRouterKey.ForgotPassword);
};

export const getRegisterUrl = () => {
    return siteRouterManager.getPath(SiteRouterKey.Register);
};

export const getSignInLaunchUrl = (suiteId?: SuiteId) => {
    const url = siteRouterManager.getPath(SiteRouterKey.SignInLaunch);
    return isNumber(suiteId)
        ? insertURLQuery(url, [
              {
                  key: URL_PARAMETER.SUITE_ID,
                  value: suiteId
              }
          ])
        : url;
};

export const getCorpSettingUrl = (tenantId = authModel.tenantId) => {
    return siteRouterManager.getPath(SiteRouterKey.CorpSetting, tenantId);
};

export const getVersionInfoUrl = (tenantId = authModel.tenantId) => {
    return siteRouterManager.getPath(SiteRouterKey.VersionInfo, tenantId);
};

/** 获取企业订单列表地址 */
export const getCorpOrderUrl = (tenantId = authModel.tenantId) => {
    return siteRouterManager.getPath(SiteRouterKey.CorpOrder, tenantId);
};

export const getCorpContactUrl = (tenantId = authModel.tenantId) => {
    return siteRouterManager.getPath(SiteRouterKey.Contact, tenantId);
};

export const getCorpCoopUrl = (tenantId = authModel.tenantId) => {
    return siteRouterManager.getPath(SiteRouterKey.CorpCoop, tenantId);
};

export const getProductCenterUrl = (tenantId = authModel.tenantId) => {
    return siteRouterManager.getPath(SiteRouterKey.ProductCenter, tenantId);
};

export const getPermissionQueryUrl = (tabKey = ':tabKey', tenantId = authModel.tenantId) => {
    return siteRouterManager.getPath(SiteRouterKey.PermissionQuery, tenantId, tabKey);
};

export const getProductSettingUrl = (tenantId = authModel.tenantId) => {
    return siteRouterManager.getPath(SiteRouterKey.ProductSetting, tenantId);
};

export const getCorpLoginUrl = (tenantId = authModel.tenantId) => {
    return siteRouterManager.getPath(SiteRouterKey.CorpLogin, tenantId);
};

export const getSignInBridgeUrl = () => {
    return siteRouterManager.getPath(SiteRouterKey.SignInBridge);
};

export const getUnsupportedTzUrl = () => {
    return siteRouterManager.getPath(SiteRouterKey.TzSupport);
};
/**
 * 获取平台数据源订单页地址
 */
export const getDatasourceOrderUrl = ({
    contentId,
    days,
    tenantId = authModel.tenantId
}: {
    contentId: string;
    days?: number;
    tenantId?: string;
}) => {
    const queryParams: { key: QueryKey; value: string | number }[] = [
        {
            key: URL_PARAMETER.ORDER_TYPE,
            value: 'platform_datasource'
        },
        {
            key: URL_PARAMETER.CONTENT_ID,
            value: contentId
        }
    ];
    if (days) {
        queryParams.push({
            key: URL_PARAMETER.DAYS,
            value: days
        });
    }
    return siteRouterManager.getQueryPath(queryParams, SiteRouterKey.OrderCreate, tenantId);
};

/**
 * 获取套件地址
 * @param suiteId 套件ID
 */
export const getSuiteUrl = (suiteId: number) => {
    return getSiteUrl(SITE_ROUTER.SUITE.HOME, suiteId);
};

/**
 * 判断当前环境是否具备指定功能
 * @param functionKey 功能 key
 */
export const hasFunction = (
    functionKey: keyof NonNullable<GlobalState['options']>['function_map']
) => {
    return !!globalState.options?.function_map?.[functionKey];
};

/**
 * 产品通讯录手机号功能是否开启
 */
export const hasFunctionContactPhone = () => {
    return get(globalState.options, 'function_map.contact_phone', true);
};

/**
 * 是否开启云币功能 目前海外版关闭，国内版打开
 */
export const hasFunctionCoin = () => {
    return get(globalState.options, 'function_map.coin', true);
};

/**
 * 产品短信验证功能是否开启
 */
export const hasFunctionPlatformSms = () => {
    return get(globalState.options, `function_map.platform_sms`, true);
};

/**
 * 是否启用用户画像收集步骤
 */
export const hasFunctionRegisterPersona = () => {
    return globalState.options?.function_map.register_persona ?? true;
};

/**
 * 商务咨询页面：升级版本、增购功能、续费链接
 */
export const getPurchaseLink = () => {
    const link = globalState.options?.customize_better_version;
    if (!link) {
        return '';
    }
    return insertURLQuery(link, [{ key: 'ext', value: authModel.tenantId }]);
};

/**
 * 是否允许创建企业
 */
export const hasFunctionTenantRegister = () =>
    get(globalState.options, 'function_map.tenant_register', true);
